/**
 * Maps legacy Formsy field props (`validations`, `validationErrors`) to RHF `validate`.
 *
 * Migration-only: when porting a screen off Formsy, you may keep existing field props
 * briefly. For new work or refactors, define a Zod schema on `Form` instead of extending
 * this module. Delete this adapter once no caller passes `validations` on Form fields.
 */
import * as z from 'zod'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import isString from 'lodash/isString'
import trim from 'lodash/trim'
import { FORM_VALIDATION_RULE } from './validationRules'
import type {
  LegacyFieldValidate,
  LegacyValidations,
  LegacyValidationErrors,
} from './formFieldTypes'

/** Matches Formsy `isSpecialWords` (letters with accents, spaces, apostrophe, hyphen). */
const SPECIAL_WORDS_PATTERN = /^[\p{L}][\p{L}\s'.-]*$/u

function parseValidationString (validations: string): Record<string, boolean | number | string> {
  const rules: Record<string, boolean | number | string> = {}
  validations.split(',').forEach((part) => {
    const trimmed = trim(part)
    if (!trimmed) {
      return
    }
    const segments = trimmed.split(':').map((segment) => trim(segment))
    const key = segments[0]
    const rawValue = segments[1]
    if (rawValue === undefined) {
      rules[key] = true
      return
    }
    const asNumber = Number(rawValue)
    rules[key] = Number.isNaN(asNumber) ? rawValue : asNumber
  })
  return rules
}

function normalizeValidations (validations?: LegacyValidations): Record<string, unknown> {
  if (!validations) {
    return {}
  }
  if (isString(validations)) {
    return parseValidationString(validations)
  }
  return { ...validations }
}

function getMessage (
  validationErrors: LegacyValidationErrors | undefined,
  key: string,
  fallback: string,
): string {
  const message = get(validationErrors, key)
  if (message) {
    return message
  }
  return fallback
}

function toFieldString (value: unknown): string {
  if (isNil(value)) {
    return ''
  }
  return String(value)
}

export type BuildLegacyFieldValidatorOptions = {
  validations?: LegacyValidations
  required?: boolean
  validationErrors?: LegacyValidationErrors
  validationError?: string
  validationErrorFormat?: (message: string, value: string) => string
  requiredMessage?: string
}

export function buildLegacyFieldValidator ({
  validations,
  required,
  validationErrors,
  validationError,
  validationErrorFormat,
  requiredMessage,
}: BuildLegacyFieldValidatorOptions): LegacyFieldValidate {
  const rules = normalizeValidations(validations)
  const formatError = validationErrorFormat || ((message: string) => message)

  return (value: unknown) => {
    const stringValue = toFieldString(value)

    if (required && stringValue.length === 0) {
      const message = requiredMessage || getMessage(
        validationErrors,
        FORM_VALIDATION_RULE.REQUIRED,
        'This field is required',
      )
      return formatError(message, stringValue)
    }

    if (rules[FORM_VALIDATION_RULE.IS_EMAIL]) {
      const emailResult = z.string().email().safeParse(stringValue)
      if (!emailResult.success) {
        const message = validationError
          || getMessage(validationErrors, FORM_VALIDATION_RULE.IS_EMAIL, 'Invalid email')
        return formatError(message, stringValue)
      }
    }

    if (rules[FORM_VALIDATION_RULE.IS_SPECIAL_WORDS]) {
      if (!SPECIAL_WORDS_PATTERN.test(stringValue)) {
        const message = getMessage(
          validationErrors,
          FORM_VALIDATION_RULE.IS_SPECIAL_WORDS,
          'Invalid value',
        )
        return formatError(message, stringValue)
      }
    }

    if (rules[FORM_VALIDATION_RULE.MIN_LENGTH] !== undefined) {
      const min = Number(rules[FORM_VALIDATION_RULE.MIN_LENGTH])
      if (stringValue.length < min) {
        const message = getMessage(
          validationErrors,
          FORM_VALIDATION_RULE.MIN_LENGTH,
          `Must be at least ${min} characters`,
        )
        return formatError(message, stringValue)
      }
    }

    if (rules[FORM_VALIDATION_RULE.MAX_LENGTH] !== undefined) {
      const max = Number(rules[FORM_VALIDATION_RULE.MAX_LENGTH])
      if (stringValue.length > max) {
        const message = getMessage(
          validationErrors,
          FORM_VALIDATION_RULE.MAX_LENGTH,
          `Must be at most ${max} characters`,
        )
        return formatError(message, stringValue)
      }
    }

    if (rules[FORM_VALIDATION_RULE.MATCH_REGEXP]) {
      const raw = rules[FORM_VALIDATION_RULE.MATCH_REGEXP]
      const pattern = raw instanceof RegExp
        ? raw
        : new RegExp(String(raw))
      if (!pattern.test(stringValue)) {
        const message = getMessage(
          validationErrors,
          FORM_VALIDATION_RULE.MATCH_REGEXP,
          'Invalid value',
        )
        return formatError(message, stringValue)
      }
    }

    return true
  }
}

export function hasClientErrorDisplay ({
  validationErrors,
  validationError,
  forceError,
}: {
  validationErrors?: LegacyValidationErrors
  validationError?: string
  forceError?: string
}): boolean {
  return Boolean(validationErrors || validationError || forceError)
}
